// Privacy Constants
// Default values and configuration for the privacy system

import type { ConsentPreferences, PrivacySettings } from '../types/privacy';

// Current privacy policy version
export const PRIVACY_VERSION = '1.0';

// Default consent preferences (privacy-first)
export const DEFAULT_CONSENT: ConsentPreferences = {
  necessary: true,        // Always required
  analytics: false,       // Opt-in required
  functional: false,      // Opt-in required  
  performance: false,     // Opt-in required
  ai_processing: false,   // Opt-in required
};

// Consent categories with descriptions
export const CONSENT_CATEGORIES = {
  necessary: {
    title: 'Necessary',
    description: 'Essential for the application to function properly',
    required: true,
    examples: ['Authentication', 'Security', 'Basic functionality']
  },
  analytics: {
    title: 'Analytics',
    description: 'Help us understand how you use the application',
    required: false,
    examples: ['Usage statistics', 'Feature adoption', 'Performance metrics']
  },
  functional: {
    title: 'Functional',
    description: 'Enhanced features and personalization',
    required: false,
    examples: ['Preferences storage', 'Enhanced UI features', 'Customization']
  },
  performance: {
    title: 'Performance',
    description: 'Monitor and improve application performance',
    required: false,
    examples: ['Error tracking', 'Performance monitoring', 'Crash reporting']
  },
  ai_processing: {
    title: 'AI Processing',
    description: 'AI-powered features and content generation',
    required: false,
    examples: ['Content suggestions', 'Auto-generation', 'Smart recommendations']
  }
} as const;

// Features that require specific consent types
export const CONSENT_REQUIRED_FEATURES = {
  // Analytics consent required
  analytics: [
    'statsig_tracking',
    'user_behavior_analytics',
    'feature_usage_tracking',
    'session_replay'
  ],
  
  // Functional consent required
  functional: [
    'enhanced_notifications',
    'preference_sync',
    'advanced_filtering',
    'custom_themes'
  ],
  
  // Performance consent required
  performance: [
    'error_reporting',
    'performance_monitoring',
    'crash_analytics',
    'debug_logging'
  ],
  
  // AI processing consent required
  ai_processing: [
    'content_generation',
    'smart_suggestions',
    'auto_tagging',
    'content_analysis',
    'resume_parsing',
    'job_matching'
  ]
} as const;

// Local storage keys
export const PRIVACY_STORAGE_KEYS = {
  ANONYMOUS_CONSENT: 'careercard_anonymous_consent',
  CONSENT_BANNER_DISMISSED: 'careercard_consent_banner_dismissed',
  PRIVACY_SETTINGS_CACHE: 'careercard_privacy_settings_cache'
} as const;

// Cache configuration
export const PRIVACY_CACHE_CONFIG = {
  TTL: 5 * 60 * 1000, // 5 minutes
  MAX_AGE: 30 * 60 * 1000 // 30 minutes max
} as const;

// Default privacy settings for new users
export const createDefaultPrivacySettings = (): PrivacySettings => ({
  consent: { ...DEFAULT_CONSENT },
  version: PRIVACY_VERSION,
  last_updated: new Date().toISOString(),
  consentTimestamp: Date.now(),
  ip_address: undefined,
  user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined
});

// Consent validation
export const isValidConsent = (consent: unknown): consent is ConsentPreferences => {
  if (typeof consent !== 'object' || consent === null) {
    return false;
  }
  const c = consent as Record<string, unknown>;
  return (
    typeof c.necessary === 'boolean' &&
    typeof c.analytics === 'boolean' &&
    typeof c.functional === 'boolean' &&
    typeof c.performance === 'boolean' &&
    typeof c.ai_processing === 'boolean'
  );
};

// Check if consent is privacy-compliant (necessary always true)
export const isPrivacyCompliant = (consent: ConsentPreferences): boolean => {
  return consent.necessary === true;
}; 